import React, { useEffect, useState } from 'react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import useProjects from '../../hooks/useProjects';
import { INTERNAL_PROJECT_ID, DEFAULT_TIME_CATEGORIES } from '../../utils/timeBillingShared';

const ACTIVITY_PRESETS = ['Meeting', 'Procurement', 'Sourcing', 'Design'];

function emptyForm() {
  return {
    date: new Date().toISOString().split('T')[0],
    projectId: INTERNAL_PROJECT_ID,
    category: '',
    employee: '',
    activity: '',
    billableHours: '',
    employeeRate: '',
    rate: '',
    description: '',
    taxable: true,
    billable: true,
  };
}

export default function GlobalTimeEntryModal({ open, mode, entry, onClose, onSaved }) {
  const apiUrl = API_BASE;
  const userId = localStorage.getItem('userId');
  const { projects } = useProjects({ activeOnly: true });

  const [team, setTeam] = useState([]);
  const [activities, setActivities] = useState(ACTIVITY_PRESETS);
  const [categories, setCategories] = useState(DEFAULT_TIME_CATEGORIES);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;

    fetch(`${apiUrl}/api/settings/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        setTeam(Array.isArray(data.team) ? data.team : []);
        const storedActivities = Array.isArray(data.timeActivities)
          ? data.timeActivities.map((a) => (typeof a === 'string' ? a.trim() : '')).filter(Boolean)
          : [];
        setActivities(Array.from(new Set([...ACTIVITY_PRESETS, ...storedActivities])));
        const storedCategories = Array.isArray(data.timeCategories)
          ? data.timeCategories.map((c) => (typeof c === 'string' ? c.trim() : '')).filter(Boolean)
          : [];
        setCategories(Array.from(new Set([...DEFAULT_TIME_CATEGORIES, ...storedCategories])));
      })
      .catch((err) => console.error('Failed to load settings', err));
  }, [open, userId, apiUrl]);

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && entry) {
      setForm({
        date: entry.date || '',
        projectId: entry.projectId || INTERNAL_PROJECT_ID,
        category: entry.category || '',
        employee: entry.teamMember || entry.employee || '',
        activity: entry.activity || '',
        billableHours: entry.time ?? entry.billableHours ?? '',
        employeeRate: entry.employeeRate ?? '',
        rate: entry.projectRate ?? entry.rate ?? '',
        description: entry.description || '',
        taxable: entry.taxable ?? true,
        billable: entry.billable ?? true,
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, mode, entry]);

  if (!open) return null;

  const handleEmployeeSelect = (value) => {
    const emp = team.find((t) => t.name === value);
    setForm((f) => ({
      ...f,
      employee: value,
      employeeRate: emp?.cost ? Number(emp.cost) : f.employeeRate,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const employeeValue = String(form.employee || '').trim();
    const activityValue = String(form.activity || '').trim();

    if (!employeeValue || !activityValue) {
      notify('Please select or enter both employee and activity.');
      return;
    }

    const isInternal = (form.projectId || INTERNAL_PROJECT_ID) === INTERNAL_PROJECT_ID;

    const payload = {
      date: form.date || new Date().toISOString().split('T')[0],
      employee: employeeValue,
      activity: activityValue,
      billableHours: Number(form.billableHours || 0),
      rate: Number(form.rate || 0),
      employeeRate: Number(form.employeeRate || 0),
      description: form.description,
      taxable: form.taxable,
      billable: form.billable,
      ...(isInternal && { category: form.category || '' }),
    };

    setSaving(true);
    try {
      let res;
      if (mode === 'edit' && entry) {
        res = await fetch(`${apiUrl}/api/timebilling/${userId}/${entry.sortKey}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${apiUrl}/api/timebilling/${userId}/${form.projectId || INTERNAL_PROJECT_ID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        notify('Failed to save time entry');
        return;
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save time entry:', err);
      notify('Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form-backdrop" onClick={onClose}>
      <div className="form-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{mode === 'edit' ? 'Edit Time Entry' : 'Log Time'}</h3>

        <form onSubmit={handleSubmit}>
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          />

          <div className="tb-select-wrap">
            <select
              className="tb-form-select"
              value={form.projectId}
              disabled={mode === 'edit'}
              onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}
            >
              <option value={INTERNAL_PROJECT_ID}>Internal / Non-Project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {form.projectId === INTERNAL_PROJECT_ID && (
            <div className="tb-select-wrap">
              <select
                className="tb-form-select"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                <option value="">Select Category</option>
                {categories.map((c, i) => (
                  <option key={i} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="tb-select-wrap">
            <select
              className="tb-form-select"
              value={form.employee}
              onChange={(e) => handleEmployeeSelect(e.target.value)}
            >
              <option value="">Select Employee</option>
              {team.map((t, i) => (
                <option key={i} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="tb-select-wrap">
            <select
              className="tb-form-select"
              value={form.activity}
              onChange={(e) => setForm((f) => ({ ...f, activity: e.target.value }))}
            >
              <option value="">Select Activity</option>
              {activities.map((a, i) => (
                <option key={i} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          <input
            type="number"
            step="0.01"
            placeholder="Hours"
            value={form.billableHours}
            onChange={(e) => setForm((f) => ({ ...f, billableHours: e.target.value }))}
          />

          <input
            type="number"
            step="0.01"
            placeholder="Employee Rate (Cost)"
            value={form.employeeRate}
            onChange={(e) => setForm((f) => ({ ...f, employeeRate: e.target.value }))}
          />

          <input
            type="number"
            step="0.01"
            placeholder="Client Rate"
            value={form.rate}
            onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
          />

          <textarea
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={form.taxable}
              onChange={(e) => setForm((f) => ({ ...f, taxable: e.target.checked }))}
            />
            Taxable?
          </label>

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={form.billable}
              onChange={(e) => setForm((f) => ({ ...f, billable: e.target.checked }))}
            />
            Billable to client?
          </label>

          <div className="form-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" disabled={saving}>
              {mode === 'edit' ? 'Save' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
